<template>
  <div class="tracking-layer">
    <div class="tracking-layer-header">
      <div class="layer-title">
        <i class="icon icon--tracking-default"></i>
        <p>{{ $t('tracking.trackingLayer.title') }}</p>
      </div>
      <template v-if="currentTab === 'member' && isSampling">
        <div class="sampling">
          <div class="pv-sampling-tag">
            {{
              $t("dashboard.samplingTag", {
                data: Math.min(domain.preSamplingRate, domain.postSamplingRate),
              })
            }}
          </div>
          <div class="notice">
            <i class="icon icon--alarm-blue"></i>
            {{ $t('tracking.trackingLayer.sampling') }}
          </div>
        </div>
      </template>
      <div class="notice" v-if="currentTab === 'guest' && noMember">
        <i class="icon icon--alarm-blue"></i>
        {{ $t('tracking.trackingLayer.allGuest') }}
      </div>
    </div>
    <div class="tracking-layer-body">
      <div class="toolbar">
        <ui-tabs :item-list="tabList" :on="currentTab" @select="onTabChange" />
        <div class="toolbar-member" v-if="currentTab === 'member'">
          <div class="member-count">
            <div class="count-wrap id">
              <span class="label">ID</span>
              <div class="value">
                <span
                  v-if="(isAllSelectActivated && excludedMembers.length === 0) || (!isAllSelectActivated && selectedMembers.length === 0)"
                  :class="{ selected: selectedMembers.length === metaData.totalCount || (isAllSelectActivated && excludedMembers.length === 0)}"
                >
                  {{ metaData.totalCount | thousand }}
                </span>
                <template v-else>
                  <span class="selected" v-if="isAllSelectActivated">{{ metaData.totalCount - excludedMembers.length | thousand }}</span>
                  <span class="selected" v-else>{{ selectedMembers.length | thousand }}</span>
                  <span class="entire">/ {{ metaData.totalCount | thousand }}</span>
                </template>
              </div>
            </div>
            <div class="count-wrap logging">
              <span class="label">{{ $t('tracking.user.logging') }}</span>
              <span class="value">{{ metaData.loggedInCount | thousand }}</span>
            </div>
            <div class="count-wrap login">
              <span class="label">{{ $t('tracking.user.login') }}</span>
              <span class="value">{{ metaData.newlyLoggedInCount | thousand }}</span>
            </div>
          </div>
          <div class="control-wrap">
            <div class="search-wrap">
              <div v-if="searchedKeyword && !listLoading" class="search-count">
                <em>{{ memberList.length }}</em
                >{{ $t("common.searchResult") }}
              </div>
              <div :class="['searchType-input', { searching: searchFocusing || searchedKeyword }]">
                <input
                  type="text"
                  ref="searchInput"
                  :placeholder="$t('tracking.trackingLayer.searchPlaceHolder')"
                  v-model="keyword"
                  @keyup.enter="onSearch"
                  @focus="searchFocusing = true"
                  @blur="onSearchBlur"
                />
                <button class="search-btn" @click.stop="onSearch">
                  <i class="icon icon--search size-16"></i>
                </button>
                <button
                  class="clear-btn"
                  v-show="keyword.length > 0"
                  @click.stop="onClearSearch"
                >
                  <i class="icon icon--close14"></i>
                </button>
              </div>
            </div>
            <div class="slider">
              <button
                class="slider-btn minus"
                @click="scale > 0 && adjustScale(-1)"
              >
                <i class="icon icon--minus size-12"></i>
              </button>
              <input type="range" min="0" max="4" v-model.number="scale" step="1" />
              <button
                class="slider-btn minus"
                @click="scale < 4 && adjustScale(1)"
              >
                <i class="icon icon--plus size-12"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div :class="['member-list', { 'scrolled-x': isListScrollingX }]" v-if="currentTab === 'member'" ref="listContainer">
        <table :class="`scale-${scale}`">
          <thead>
            <tr>
              <th class="th-checkbox fixed">
                <span>
                  <ui-checkbox
                    :value="(isAllSelectActivated && !excludedMembers.length) || selectedMembers.length === metaData.totalCount"
                    :unselect="isAllSelectActivated || selectedMembers.length"
                    @input="selectAll()"
                  />
                </span>
              </th>
              <th
                v-for="column in columns"
                :key="`header-${column.value}`"
                :class="[`th-${column.value}`, { fixed: fixedColumns.includes(column) },]"
              >
                <span>
                  <button
                  :class="['mark-button', {disabled: !markedColumns.includes(column) && markedColumns.length === markLimit}]"
                  v-if="!fixedColumns.includes(column)"
                  @click="markColumn(column)"
                  >
                    <i class="icon icon--fix-red" v-if="markedColumns.includes(column)"></i>
                    <i class="icon icon--fix-disabled" v-else-if="markedColumns.length === markLimit"></i>
                    <i class="icon icon--fix-gray" v-else></i>
                  </button>
                  <span>{{ column.name[$i18n.locale] }}</span>
                  <button
                    v-if="!notSortable.includes(column.value)"
                    class="sort-button"
                    :class="{
                      active: memberList.length && listOption.sortColumn === column.value,
                      asc: !listOption.isDescending,
                      desc: listOption.isDescending,
                    }"
                    @click="changeSort(column.value)"
                  >
                    <i class="icon icon--sort-asc"></i>
                    <i class="icon icon--sort-desc"></i>
                  </button>
                </span>
              </th>
            </tr>
          </thead>
          <tbody v-if="memberList.length">
            <tr v-for="(item, index) in memberList" :key="item.id">
              <td class="td-checkbox fixed">
                <span>
                  <ui-checkbox
                    :value="checkIsSelected(item)"
                    @input="selectMember(item)"
                  />
                </span>
              </td>
              <td
                v-for="column in columns"
                :key="`data-${index}-${column.value}`"
                :class="[
                  `td-${column.value}`,
                  { fixed: fixedColumns.includes(column) },
                ]"
              >
                <span>
                  <template v-if="column.value === 'index'">
                    {{ index + 1 }}
                  </template>
                  <template v-else-if="item[column.value] != null">
                    <template v-if="column.value === searchColumn">
                      <text-highlight
                        :searchKeyword="searchedKeyword"
                        :textItem="item[column.value]"
                        accentColor="#F46200"
                      ></text-highlight>
                    </template>
                    <template v-else-if="!isNaN(Number(item[column.value]))">
                      {{ item[column.value] | thousand }}
                    </template>
                    <template v-else-if="column.type === 'date'">
                      {{ String(item[column.value]).split(' ')[0] }}
                    </template>
                    <template v-else-if="column.type === 'dateTime'">
                      {{ String(item[column.value]).split('.')[0] }}
                    </template>
                    <template v-else>
                      {{ item[column.value] }}
                    </template>
                  </template>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="loading"
              v-show="listLoading">
          <span class="loading-circle">
            <svg x="0px" y="0px" viewBox="0 0 150 150">
              <circle class="loading-inner" cx="75" cy="75" r="60"/>
            </svg>
          </span>
        </div>
        <div ref="observer" class="observer"></div>
        <div v-if="!memberList.length && !listLoading && searchedKeyword" class="no-data">
          <i class="icon loading--empty"></i>
          <p class="no-data-title">{{ $t("common.noResult") }}</p>
          <p class="no-data-description">
            {{ $t("common.noResultSearch") }}
          </p>
          <p class="no-data-keyword">'{{ searchedKeyword }}'</p>
        </div>
      </div>
      <div class="guest" v-else>
        <div class="guest-header">
          <h1 class="guest-header-title" v-html="$t('tracking.trackingLayer.guestTab.title')"></h1>
          <div class="guest-header-summary">
            <p class="summary-item">
              <span class="target">{{ $t('tracking.trackingLayer.guestTab.target') }}:</span>
              <span class="value">{{ $t('tracking.trackingLayer.guestTab.targetValue') }}</span>
            </p>
            <p class="summary-item">
              <span class="session">{{ $t('tracking.trackingLayer.guestTab.session') }}:</span>
              <span class="value">{{ metaData.guestSessionCnt | thousand }}</span>
            </p>
          </div>
        </div>
        <div class="guest-guide">
          <div class="guest-guide-header">
            <h2 class="main-title" v-html="$t('tracking.trackingLayer.guestTab.guideTitle')"></h2>
            <p class="sub-title" v-html="$t('tracking.trackingLayer.guestTab.guideSubTitle')"></p>
          </div>
          <div class="guest-guide-body">
            <div class="guide">
              <p class="guide-title" v-html="$t('tracking.trackingLayer.guestTab.guideListTitle')"></p>
              <ul class="guide-list">
                <li v-html="$t('tracking.trackingLayer.guestTab.guideListItem1')"></li>
                <li v-html="$t('tracking.trackingLayer.guestTab.guideListItem2')"></li>
                <li v-html="$t('tracking.trackingLayer.guestTab.guideListItem3')"></li>
              </ul>
            </div>
            <i class="icon img--tracking-bg"></i>
          </div>
        </div>
      </div>
    </div>
    <div class="tracking-layer-footer">
      <button
        class="btf-button outline primary icon"
        :class="{ disabled: currentTab === 'member' && (!selectedMembers.length && !isAllSelectActivated) }"
        @click="setTracking"
      >
        <i class="icon icon--tracking-default size-14"></i>
        Tracking
      </button>
    </div>
    <button class="close-button" @click="$emit('close')">
      <i class="icon icon--close-size16"></i>
    </button>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getNodeUsers } from '@/api/journey'
import { getTargetNodeUsers } from '@/api/targeting'
import { getTrendNodeUsers } from '@/api/trend'
import { getJourneyNodeKey } from '@/libs/utils'
import UiTabs from '@/components/ui/UiTabs'
import UiCheckbox from '@/components/ui/UiCheckbox'
import TextHighlight from '@/components/ui/TextHighlight'

export default {
  name: 'TrackingLayer',
  components: { UiTabs, UiCheckbox, TextHighlight },
  props: {
    nodeData: {
      required: true
    }
  },
  data () {
    return {
      isInitialized: false,
      currentTab: 'member',
      listLoading: false,
      isFullyLoaded: false,
      listOption: {
        page: 0,
        itemsPerPage: 50,
        isDescending: false,
        sortColumn: 'id'
      },
      searchColumn: 'displayId',
      fixedColumns: [
        { name: { ko: 'No', en: 'No', ja: 'No' }, value: 'index' },
        { name: { ko: 'ID', en: 'ID', ja: 'ID' }, value: 'displayId' }
      ],
      movableColumns: [
        { name: { ko: 'Session', en: 'Session', ja: 'Session' }, value: 'sessionCount' },
        { name: { ko: 'Logging', en: 'Logging', ja: 'Logging' }, value: 'loggedInCount' },
        { name: { ko: 'Login', en: 'Login', ja: 'Login' }, value: 'newlyLoggedInCount' }
      ],
      markedColumns: [],
      markLimit: 5,
      notSortable: ['index'],
      memberList: [],
      selectedMembers: [],
      excludedMembers: [],
      metaData: {},
      keyword: '',
      searchedKeyword: '',
      searchFocusing: false,
      scale: 4,
      observer: null, // Intersection Observer instance
      isListScrollingX: false,
      isAllSelectActivated: false,
      memberCacheKey: null,
      sessionCacheKey: null
    }
  },
  computed: {
    ...mapState([
      'journeyPeriod',
      'journeyOptions',
      'journeyTargetingOptions',
      'domain',
      'isSampling'
    ]),
    isTrackingDisabled () {
      return false
    },
    columns () {
      return [
        ...this.fixedColumns,
        ...this.markedColumns,
        ...this.movableColumns.filter(
          (item) => !this.markedColumns.includes(item)
        )
      ]
    },
    noMember () {
      return this.metaData.loginSessionCnt === 0
    },
    noGuest () {
      return this.metaData.guestSessionCnt === 0
    },
    tabList () {
      const tabs = [
        {
          name: 'member',
          label: this.$t('tracking.member'),
          value: 'member',
          disabled: this.noMember
        },
        {
          name: 'guest',
          label: this.$t('tracking.guest'),
          value: 'guest',
          disabled: this.noGuest
        }
      ]

      const memberRatio = parseFloat((this.metaData.loginSessionCnt / this.metaData.totalSessionCnt * 100).toFixed(1))
      const guestRatio = parseFloat((this.metaData.guestSessionCnt / this.metaData.totalSessionCnt * 100).toFixed(1))
      tabs[0].label += ` (${isNaN(memberRatio) ? '--' : memberRatio}%)`
      tabs[1].label += ` (${isNaN(guestRatio) ? '--' : guestRatio}%)`

      return tabs
    },
    isForStartNode () {
      if (!this.nodeData) return false
      if (this.nodeData.isTrend) {
        return this.nodeData.isForStartNode
      }
      const key = getJourneyNodeKey(this.nodeData, this.journeyOptions.isCustomNode)
      return key === '-1'
    }
  },
  mounted () {
    this.initIntersectionObserver()
    if (this.$refs.listContainer) {
      this.$refs.listContainer.addEventListener('scroll', this.handleScroll)
    }
  },
  created () {
    const customUserColumn = this.domain ? this.domain.beuCustomUserColumn : null
    if (customUserColumn) {
      const nonMainKeyCols = customUserColumn.filter(col => !col.isMainKey)
      this.movableColumns = this.movableColumns.concat(nonMainKeyCols)
    }
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.$refs.listContainer) {
      this.$refs.listContainer.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    initIntersectionObserver () {
      const options = {
        root: this.$refs.listContainer,
        rootMargin: '400px',
        threshold: 0.1
      }

      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.getUserList()
        }
      }, options)

      if (this.$refs.observer) {
        this.observer.observe(this.$refs.observer)
      }
    },
    async getUserList () {
      if (!this.nodeData || this.isFullyLoaded || this.listLoading) return

      this.listLoading = true
      const payload = {
        device: this.journeyOptions.device,
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        matchType: this.journeyOptions.matchType,
        exist: this.journeyOptions.exist,
        refer: this.journeyOptions.refer,
        aiRefererIds: this.journeyOptions.aiRefererIds,
        utm: this.journeyOptions.utm,
        survey: this.journeyOptions.survey,
        isCustomNode: this.journeyOptions.isCustomNode,
        subSid: this.journeyOptions.subSid,
        firstPathname: this.journeyOptions.firstPath,
        postSamplingRate: this.domain.postSamplingRate,
        pageId: this.listOption.page,
        pageSize: this.listOption.itemsPerPage,
        searchText: this.keyword || null,
        sortKey: this.listOption.sortColumn,
        isReversed: this.listOption.isDescending,
        isTotalSessNecessary: false
      }

      if (this.nodeData.trackingData) {
        const { type, isFromFunnel = false, isAllSelected, excludedMembers = [], selectedMembers = [], memberCacheKey, sessionCacheKey } = this.nodeData.trackingData

        if (type === 'member') {
          payload.memberCacheKey = memberCacheKey
          payload.isAllMemberSelected = isAllSelected && isFromFunnel
          payload.excludedMembers = excludedMembers.map(item => item.id)
          payload.selectedMembers = isAllSelected ? [] : selectedMembers.map(item => item.id)
        } else {
          payload.isAllNonMemberSelected = isAllSelected
          payload.sessionCacheKey = sessionCacheKey
        }
      }

      if (this.nodeData.isTargeting) {
        payload.redisKey = this.nodeData.redisKey
        payload.targetNodeId = this.nodeData.targetNodeId
        payload.targetNodeKey = this.journeyOptions.isCustomNode || this.journeyOptions.matchType === 'exact' ? this.journeyTargetingOptions.targetNodeKey : null
        payload.selectedNodeId = this.journeyOptions.isCustomNode ? this.nodeData.customId : this.nodeData.gUrlId
        payload.startFromHead = this.nodeData.lookupOption === 'landing'
        payload.right = this.journeyTargetingOptions.direction === 'forward'
        payload.seq = this.nodeData._seq
        payload.tempDFKey = this.nodeData.targetNodeRedisKey

        getTargetNodeUsers(payload)
          .then(res => {
            this.setUserListData(res)
          })
          .catch(e => {
            console.log(e)
          })
      } else if (this.nodeData.isTrend) {
        payload.gUrlId = this.nodeData.gUrlId
        payload.nodeKey = this.nodeData.nodeKey
        payload.sequence = this.nodeData.sequence
        payload.trendMap = this.nodeData.trendMap
        payload.isForStartNode = this.isForStartNode
        payload.isForEtcNodes = this.nodeData.isForEtcNodes

        getTrendNodeUsers(payload)
          .then(res => {
            this.setUserListData(res)
          })
          .catch(e => {
            console.log(e)
          })
      } else {
        payload.nodeKey = getJourneyNodeKey(this.nodeData, this.journeyOptions.isCustomNode)
        payload.treeId = this.nodeData.id
        payload.pathStr = this.nodeData.pathStr
        payload.isForStartNode = this.isForStartNode
        payload.isParent = this.domain.isParent

        getNodeUsers(payload)
          .then(res => {
            this.setUserListData(res)
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    setUserListData (data) {
      this.listLoading = false
      if (!this.isInitialized && data.total_sess_count) {
        this.metaData = {
          loginSessionCnt: data.login_sess_count,
          guestSessionCnt: data.no_login_sess_count,
          totalSessionCnt: data.total_sess_count,
          totalCount: data.matched_users_count,
          loggedInCount: data.total_logged_in_count,
          newlyLoggedInCount: data.total_newly_logged_in_count
        }
        if (this.noMember) {
          this.currentTab = 'guest'
        }
        this.memberCacheKey = data.members_cache_key
        this.sessionCacheKey = data.sessions_cache_key
        this.isInitialized = true
      }
      const newList = data.users
      if (newList) {
        this.memberList = [...this.memberList, ...newList]
        this.listOption.page++
      }
      if (!newList || newList.length < this.listOption.itemsPerPage) {
        this.isFullyLoaded = true
      }
    },
    onTabChange (item) {
      this.currentTab = item
    },
    setTracking () {
      const data = {
        type: this.currentTab,
        isAllSelected: this.isAllSelectActivated || (this.currentTab === 'guest' && this.isForStartNode),
        selectedMembers: this.selectedMembers,
        excludedMembers: this.excludedMembers,
        memberCacheKey: this.memberCacheKey,
        sessionCacheKey: this.sessionCacheKey
      }

      if (this.isAllSelectActivated && this.excludedMembers.length === this.metaData.totalCount - 1) {
        const excludedIds = new Set(this.excludedMembers.map(item => item.id))
        const selectedOne = this.memberList.find(item => !excludedIds.has(item.id))
        data.selectedMembers = [selectedOne]
      }

      this.$emit('setTracking', data)
    },
    changeSort (column) {
      const { listOption } = this
      listOption.isDescending = listOption.sortColumn === column ? !listOption.isDescending : true
      listOption.sortColumn = column
      this.isFullyLoaded = false

      this.memberList = []
      this.listOption.page = 0
      this.getUserList()
    },
    markColumn (col) {
      this.markedColumns = this.markedColumns.includes(col)
        ? this.markedColumns.filter((item) => item !== col)
        : [...this.markedColumns, col]
    },
    selectMember (member) {
      if (this.isAllSelectActivated) {
        if (this.excludedMembers.find(item => item.id === member.id)) {
          this.excludedMembers = this.excludedMembers.filter((item) => item.id !== member.id)
        } else {
          this.excludedMembers = [...this.excludedMembers, member]
          if (this.excludedMembers.length === this.metaData.totalCount) {
            this.isAllSelectActivated = false
            this.excludedMembers = []
          }
        }
      } else {
        if (this.selectedMembers.find(item => item.id === member.id)) {
          this.selectedMembers = this.selectedMembers.filter((item) => item.id !== member.id)
        } else {
          this.selectedMembers = [...this.selectedMembers, member]
        }
      }
    },
    selectAll () {
      this.isAllSelectActivated = !this.isAllSelectActivated || this.excludedMembers.length > 0
      this.selectedMembers = []
      this.excludedMembers = []
    },
    checkIsSelected (member) {
      if (this.isAllSelectActivated) {
        return !this.excludedMembers.find(item => item.id === member.id)
      }
      return this.selectedMembers.find(item => item.id === member.id)
    },
    adjustScale (value) {
      this.scale += value
    },
    onSearch () {
      if (!this.keyword.length) {
        this.onClearSearch()
      } else {
        this.memberList = []
        this.selectedMembers = []
        this.searchedKeyword = this.keyword
        this.listOption.page = 0
        this.isFullyLoaded = false
        this.getUserList()
      }
    },
    onSearchBlur () {
      setTimeout(() => {
        if (!this.searchedKeyword) {
          this.searchFocusing = false
          this.keyword = ''
        }
      }, 100)
    },
    onClearSearch () {
      const searchInput = this.$refs.searchInput
      if (searchInput) searchInput.blur()
      this.keyword = ''
      this.searchFocusing = false
      if (this.searchedKeyword) {
        this.listOption.page = 0
        this.searchedKeyword = ''
        this.memberList = []
        this.isFullyLoaded = false
        this.getUserList()
      }
    },
    handleScroll () {
      if (this.$refs.listContainer) {
        this.isListScrollingX = this.$refs.listContainer.scrollLeft > 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tracking-layer {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 1170px;
  padding: 28px 28px 24px;
  border-radius: 14px;
  background-color: #fff;
  font-weight: 400;
  color: #2f2f2f;
  box-sizing: border-box;

  .close-button {
    position: absolute;
    top: 15px;
    right: 15px;
  }
  &-header {
    position: relative;
    @include flexYcenter;
    gap: 26px;
    margin-bottom: 25px;
    .layer-title {
      @include flexYcenter;
      gap: 8px;
      color: #2f2f2f;
      font-size: 15px;
      font-weight: 600;
    }
    .sampling {
      @include flexYcenter;
      gap: 8px;
      .pv-sampling-tag {
        color: #fff;
        font-size: 12px;
        font-weight: 400;
        padding: 5px;
        border-radius: 4px;
        background: linear-gradient(
          93deg,
          #0074ff 0%,
          #1783ba 63.5%,
          #57ab00 100%
        );
      }
    }
    .notice {
      @include flexYcenter;
      gap: 4px;
      color: #666;
      font-size: 13px;
    }
  }
  &-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 15px;

    .toolbar {
      @include flexYcenter;
      gap: 24px;
      .ui-tabs {
        flex-shrink: 0;
      }

      &-member {
        @include flexYcenter;
        width: 100%;
        justify-content: space-between;
        .member-count {
          display: flex;
          align-items: center;
          gap: 25px;
          .count-wrap {
            position: relative;
            display: flex;
            align-items: center;
            gap: 8px;
            .label {
              font-size: 14px;
              font-weight: 600;
            }
            .value {
              font-size: 16px;
              font-weight: 600;
              color: #444;
            }
            &.id {
              .label {
                color: $brandColor;
              }
              .value {
                font-size: 24px;
                font-weight: 600;
              }
              .selected {
                color: $brandColor;
              }
              .entire {
                color: #666;
                font-size: 18px;
                padding: 0 0 2px 8px;
              }
            }
            &.logging {
              .label {
                color: #FF708A;
              }
            }
            &.login {
              .label {
                color: #AC0033;
              }
            }
            &:not(:last-child):after {
              content: "";
              position: absolute;
              top: 50%;
              right: -12px;
              display: block;
              width: 1px;
              height: 16px;
              background-color: #D1D1D1;
              transform: translateY(-50%);
            }
          }
        }
        .control-wrap {
          @include flexYcenter;
          gap: 25px;
        }
        .search-wrap {
          position: relative;
          display: flex;
          align-items: center;
          gap: 16px;
          &::after {
            @include positionYcenter;
            content: "";
            right: -12px;
            width: 1px;
            height: 16px;
            background-color: #d1d1d1;
          }
          .search-count {
            font-size: 14px;
            font-weight: 400;
            color: #666;
            em {
              font-weight: 500;
              color: #f46200;
            }
          }
          .searchType-input {
            display: flex;
            align-items: center;
            position: relative;
            width: 32px;
            height: 32px;
            border-radius: 5px;
            box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08),
              0px 1px 2px 0px rgba(0, 0, 0, 0.2);
            transition: width 200ms;
            & * {
              transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
            }
            &.disabled input {
              background-color: #d7d7d7;
              opacity: 0.3;
              pointer-events: none;
            }
            input {
              width: 100%;
              height: 100%;
              padding: 9px 50px 9px 10px;
              border: solid 1px transparent;
              background-color: transparent;
              font-size: 13px;
              color: #2f2f2f;
              box-sizing: border-box;
              z-index: 2;
              cursor: pointer;
              &::placeholder {
                color: transparent;
              }
            }
            button {
              @include flexXYcenter;
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              width: 20px;
              height: 20px;
              &.search-btn {
                width: 20px;
                height: 20px;
                right: 6px;
              }
              &.clear-btn {
                right: 32px;
              }
            }

            &.searching {
              width: 152px;
              border: 1px solid #0096ff;
              input {
                cursor: text;
                &::placeholder {
                  color: #bbb;
                }
              }
              button {
                z-index: 2;
              }
            }
          }
        }
        .slider {
          @include flexYcenter;
          gap: 6px;
          padding: 9px 6px;
          border-radius: 5px;
          box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08),
            0px 1px 2px 0px rgba(0, 0, 0, 0.2);

          input[type="range"] {
            appearance: none;
            background-color: transparent;
            width: 73px;
            margin: 0;
            cursor: pointer;
          }

          input[type="range"]::-webkit-slider-runnable-track {
            position: relative;
            height: 4px;
            background-color: #e9e9e9;
            border-radius: 2px;
            box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.15) inset;
          }

          input[type="range"]::-moz-range-track {
            position: relative;
            height: 4px;
            background-color: #e9e9e9;
            border-radius: 2px;
            box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.15) inset;
          }

          input[type="range"]::-webkit-slider-thumb {
            box-sizing: border-box;
            appearance: none;
            position: relative;
            background-color: #fff;
            border-radius: 100%;
            width: 11px;
            height: 11px;
            top: 50%;
            translate: 0 -50%;
            box-shadow: 0px 2px 5px -2px #00000024, 0px 0px 0px 1px #00000029;
          }

          input[type="range"]::-moz-range-thumb {
            box-sizing: border-box;
            appearance: none;
            position: relative;
            background-color: #fff;
            border-radius: 100%;
            width: 11px;
            height: 11px;
          }

          input[type="range"]::-moz-range-progress {
            height: 4px;
            background-color: e9e9e9;
            border-radius: 2px;
          }

          &-btn {
            display: flex;
            &:hover {
              border-radius: 2px;
              background: rgba(0, 0, 0, 0.06);
            }
          }
        }
      }
    }
    .member-list {
      display: flex;
      flex-direction: column;
      height: 493px;
      overflow: auto;

      table {
        min-width: 100%;
        table-layout: fixed;
        font-size: 13px;

        &.scale-4 {
          thead {
            tr {
              height: 50px;
              th {
                padding: 6px 12px;
              }
            }
          }
          tbody {
            tr {
              height: 47px;
              td {
                padding: 0 12px;
                font-size: 13px;
              }
            }
          }
        }
        &.scale-3 {
          thead {
            tr {
              height: 40px;
              th {
                padding: 6px 12px;
              }
            }
          }
          tbody {
            tr {
              height: 40px;
              td {
                padding: 0 12px;
                font-size: 13px;
              }
            }
          }
        }
        &.scale-2 {
          thead {
            tr {
              height: 32px;
              th {
                padding: 6px 12px;
              }
            }
          }
          tbody {
            tr {
              height: 32px;
              td {
                padding: 0 12px;
                font-size: 13px;
              }
            }
          }
        }
        &.scale-1 {
          thead {
            tr {
              height: 28px;
              th {
                padding: 4px 12px;
              }
            }
          }
          tbody {
            tr {
              height: 25px;
              td {
                padding: 0 12px;
                font-size: 11px;
              }
            }
          }
        }
        &.scale-0 {
          thead {
            tr {
              height: 28px;
              th {
                padding: 4px 12px;
              }
            }
          }
          tbody {
            tr {
              height: 20px;
              td {
                padding: 0 12px;
                font-size: 10px;
              }
            }
          }
        }
        thead {
          tr {
            width: 100%;
            position: sticky;
            top: 0;
            z-index: 2;

            th {
              text-align: center;
              font-weight: 500;
              color: #fff;
              white-space: nowrap;
              vertical-align: bottom;
              box-sizing: border-box;
              background-color: #555;

              > span {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 4px;
                height: 20px;
              }

              &:first-child {
                border-radius: 5px 0 0 5px;
                padding-left: 17px;
              }
              &:last-child {
                border-radius: 0 5px 5px 0;
                padding-right: 15px;
              }

              &.fixed {
                position: sticky;
                background-color: #333;
                z-index: 3;
                &:nth-of-type(1) {
                  width: 47px;
                  left: 0;
                  > span {
                    width: 18px;
                  }
                }
                &:nth-of-type(2) {
                  width: 68px;
                  left: 47px;
                  > span {
                    width: 44px;
                  }
                }
                &:nth-of-type(3) {
                  width: 160px;
                  left: 115px;
                  > span {
                    width: 136px;
                  }
                }
              }

              &.th-loggedInCount {
                background-color: #FF708A;
                min-width: 110px;
              }
              &.th-newlyLoggedInCount {
                background-color: #8D002A;
                min-width: 110px;
              }

              .mark-button {
                display: flex;
                padding: 2px 0;
                &.disabled {
                  pointer-events: none;
                }
              }

              .sort-button {
                display: inline-block;
                width: 14px;
                height: 18px;
                margin-left: 1px;
                vertical-align: middle;
                border-radius: 5px;
                transition: background-color 0.1s ease, box-shadow 0.1s ease;

                i.icon {
                  float: left;
                  opacity: 0.3;
                  transition: opacity 0.15s ease;
                }

                &.active {
                  &.asc {
                    .icon--sort-asc {
                      opacity: 1;
                    }
                  }

                  &.desc {
                    .icon--sort-desc {
                      opacity: 1;
                    }
                  }
                }

                &.active {
                  background-color: #272727;
                  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.146);
                }
              }
            }
          }
        }
        tbody {
          max-height: 440px;
          tr {
            border-bottom: 1px solid #ebebeb;
            width: 100%;
            td {
              color: #444;
              box-sizing: border-box;
              span {
                @include textEllipsis;
              }
              &:first-child {
                padding-left: 17px;
              }

              &.fixed {
                position: sticky;
                background-color: #fff;
                z-index: 1;
                &:nth-of-type(1) {
                  left: 0;
                  width: 47px;
                  > span {
                    width: 18px;
                  }
                }
                &:nth-of-type(2) {
                  left: 47px;
                  width: 68px;
                  > span {
                    width: 44px;
                  }
                  &:after {
                    position: absolute;
                    top: 0;
                    right: -1px;
                    content: "";
                    width: 4px;
                    height: 100%;
                    background-color: #fff;
                  }
                }
                &:nth-of-type(3) {
                  left: 115px;
                  width: 160px;
                  > span {
                    width: 136px;
                  }
                  &:after {
                    position: absolute;
                    top: 0;
                    left: 100%;
                    content: "";
                    width: 4px;
                    height: calc(100% + 1px);
                    border-left: 1px solid #cdcdcd;
                    background: linear-gradient(
                      90deg,
                      rgba(0, 0, 0, 0.15) 0%,
                      rgba(0, 0, 0, 0) 100%
                    );
                    box-sizing: border-box;
                  }
                }
              }
              span {
                position: relative;
                display: block;
              }
            }
          }
        }

        /deep/ .ui_checkbox {
          width: 18px;
          height: 18px;
          margin: 0;
          .text {
            margin: 0;
          }
        }
      }

      &.scrolled-x {
        table tbody tr td.fixed:nth-of-type(3)::after {
          width: 12px;
        }
      }

      .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        position: sticky;
        left: 50%;
        transform: translateX(-50%);
        width: 45px;
        height: 100%;
        min-height: 70px;
        border-radius: 8px;

        .loading-circle {
          width: 45px;
          height: 45px;
          animation: loading 3s linear infinite;
          transition: opacity 0.3s ease;

          svg {
            height: 100%;
          }

          .loading-inner {
            stroke: {
              dashoffset: 0;
              dasharray: 300;
              width: 24;
              miterlimit: 10;
              linecap: round;
            }

            animation: loading-circle 2s linear infinite;
            stroke: $brandColor;
            fill: transparent;
          }
        }
      }

      .no-data {
        position: absolute;
        top: calc(50% + 20px);
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        .no-data-title {
          font-size: 20px;
          font-weight: 600;
          color: #2f2f2f;
        }

        .no-data-description {
          font-size: 14px;
          line-height: 1.5;
          font-weight: 400;
          color: #767676;
        }

        .no-data-keyword {
          font-size: 16px;
          font-weight: 600;
          line-height: 1.5;
          color: #ff8400;
        }
      }
    }
    .guest {
      display: flex;
      flex-direction: column;
      gap: 12px;
      height: 100%;
      padding-top: 9px;
      &-header {
        display: flex;
        flex-direction: column;
        gap: 27px;
        padding-left: 8px;

        &-title {
          color: #444;
          font-size: 26px;
          font-weight: 600;
        }
        &-summary {
          @include flexYcenter;
          gap: 28px;
          .summary-item {
            @include flexYcenter;
            gap: 8px;
            .target {
              color: #444;
              font-size: 13px;
              font-weight: 600;
            }
            .session {
              color: #0e90a5;
              font-size: 13px;
              font-weight: 600;
            }
            .value {
              color: #777;
              font-size: 16px;
              font-weight: 600;
            }
          }
        }
      }
      &-guide {
        display: flex;
        flex-direction: column;
        gap: 30px;
        width: 100%;
        height: 100%;
        padding: 44px 32px 32px;
        border-radius: 5px;
        border: 1px solid #d7d7d7;
        background-color: #f4f4f4;
        box-sizing: border-box;
        &-header {
          display: flex;
          align-items: flex-end;
          gap: 33px;
          word-break: break-word;
          .main-title {
            position: relative;
            flex-shrink: 0;
            color: #6218cf;
            font-size: 30px;
            font-weight: 600;
            line-height: 120%;
            &:after {
              position: absolute;
              top: 0;
              right: -16px;
              content: "";
              width: 1px;
              height: 100%;
              background: #d9d9d9;
            }
          }
          .sub-title {
            width: 468px;
            color: #444;
            font-size: 16px;
            font-weight: 500;
            line-height: 150%;
          }
        }
        &-body {
          display: flex;
          justify-content: space-between;
          .guide {
            padding-top: 14px;
            &-title {
              margin-bottom: 19px;
              color: #444;
              font-size: 16px;
              font-weight: 600;
            }
            &-list {
              display: flex;
              flex-direction: column;
              gap: 4px;
              color: #444;
              font-size: 14px;
              line-height: 150%;
              list-style: square;
              padding-left: 15px;
              li::marker {
                color: #888;
              }
            }
          }
        }
      }
    }
  }
  &-footer {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 24px 28px 0;

    /deep/ .btf-button.disabled {
      filter: grayscale(0);
      opacity: 0.3;
    }
  }

  .observer {
    height: 20px;
  }
}

@keyframes loading {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes loading-circle {
  0% {
    stroke-dashoffset: 600;
  }
  50% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: -600;
  }
}
</style>
